/**
 * useInteractionTracking Hook
 *
 * React hook for easy integration of interaction tracking in components.
 * Provides convenient methods for tracking clicks, navigation, and form submissions
 * with automatic context (component name, chapter, page path).
 *
 * Features:
 * - Automatic page path detection
 * - Component-level context
 * - Chapter-aware tracking
 * - Type-safe tracking methods
 *
 * Usage:
 * ```typescript
 * const MyComponent = () => {
 *   const { trackClick, trackNavigation, trackFormSubmit } = useInteractionTracking('MyComponent', 'chapter-0');
 *
 *   return (
 *     <button onClick={() => {
 *       trackClick('my-button', 'button', { action: 'start' });
 *       // ... handle click
 *     }}>
 *       Click Me
 *     </button>
 *   );
 * };
 * ```
 */

import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  interactionTracking,
  TrackInteractionParams,
  InteractionElementType,
  InteractionEventType
} from '@/services/interactionTrackingService';

/**
 * Hook options
 */
export interface UseInteractionTrackingOptions {
  /** Component name (for context) */
  componentName?: string;

  /** Chapter context (e.g., 'chapter-0', 'chapter-1') */
  chapter?: string;

  /** Lesson ID (if within a specific lesson) */
  lessonId?: string;

  /** Whether to automatically track page views on mount */
  trackPageView?: boolean;
}

/**
 * Return type for the hook
 */
export interface InteractionTrackingHook {
  /** Track a click interaction */
  trackClick: (elementId: string, elementType?: InteractionElementType, metadata?: Record<string, any>) => void;

  /** Track navigation (with automatic navigation after tracking) */
  trackNavigation: (destinationPath: string, metadata?: Record<string, any>) => void;

  /** Track navigation without automatic navigation */
  trackNavigationOnly: (destinationPath: string, metadata?: Record<string, any>) => void;

  /** Track form submission */
  trackFormSubmit: (formId: string, formData?: Record<string, any>) => void;

  /** Track modal open */
  trackModalOpen: (modalId: string, modalType?: string) => void;

  /** Track modal close */
  trackModalClose: (modalId: string, reason?: string) => void;

  /** Track tab change */
  trackTabChange: (tabId: string, tabLabel?: string) => void;

  /** Track card click */
  trackCardClick: (cardId: string, cardTitle: string, destination?: string) => void;

  /** Track file download */
  trackDownload: (fileId: string, fileName: string, fileType?: string) => void;

  /** Track generic interaction with full control */
  trackInteraction: (params: Partial<TrackInteractionParams>) => void;

  /** Current page path */
  currentPath: string;

  /** Session ID */
  sessionId: string;
}

/**
 * useInteractionTracking Hook
 *
 * @param componentName - Name of the component using the hook
 * @param chapter - Chapter context (e.g., 'chapter-0')
 * @param lessonId - Lesson identifier (optional)
 * @returns Tracking methods and utilities
 *
 * @example
 * ```typescript
 * // Basic usage
 * const { trackClick } = useInteractionTracking('Dashboard');
 *
 * // With chapter context
 * const { trackClick, trackNavigation } = useInteractionTracking('Chapter0Hub', 'chapter-0');
 *
 * // With all context
 * const { trackClick } = useInteractionTracking('LessonComponent', 'chapter-0', 'lesson-1');
 * ```
 */
export const useInteractionTracking = (
  componentName?: string,
  chapter?: string,
  lessonId?: string
): InteractionTrackingHook => {
  const location = useLocation();
  const navigate = useNavigate();

  const currentPath = location.pathname;
  const sessionId = useMemo(() => interactionTracking.getSessionId(), []);

  /**
   * Track a click interaction
   */
  const trackClick = useCallback(
    (elementId: string, elementType: InteractionElementType = 'button', metadata?: Record<string, any>) => {
      interactionTracking.trackClick(elementId, elementType, {
        componentName,
        chapter,
        lessonId,
        metadata
      });
    },
    [componentName, chapter, lessonId]
  );

  /**
   * Track navigation and then navigate
   */
  const trackNavigation = useCallback(
    (destinationPath: string, metadata?: Record<string, any>) => {
      // Track the navigation
      interactionTracking.trackNavigation(currentPath, destinationPath, chapter, metadata);

      // Then navigate
      navigate(destinationPath);
    },
    [currentPath, chapter, navigate]
  );

  /**
   * Track navigation without automatic navigation
   * Useful when you want to track but navigate manually
   */
  const trackNavigationOnly = useCallback(
    (destinationPath: string, metadata?: Record<string, any>) => {
      interactionTracking.trackNavigation(currentPath, destinationPath, chapter, metadata);
    },
    [currentPath, chapter]
  );

  /**
   * Track form submission
   */
  const trackFormSubmit = useCallback(
    (formId: string, formData?: Record<string, any>) => {
      interactionTracking.trackFormSubmit(formId, formData, {
        componentName,
        chapter,
        lessonId
      });
    },
    [componentName, chapter, lessonId]
  );

  /**
   * Track modal open
   */
  const trackModalOpen = useCallback(
    (modalId: string, modalType?: string) => {
      interactionTracking.trackModalOpen(modalId, modalType, {
        componentName,
        chapter,
        lessonId
      });
    },
    [componentName, chapter, lessonId]
  );

  /**
   * Track modal close
   */
  const trackModalClose = useCallback(
    (modalId: string, reason?: string) => {
      interactionTracking.trackModalClose(modalId, reason, {
        componentName,
        chapter,
        lessonId
      });
    },
    [componentName, chapter, lessonId]
  );

  /**
   * Track tab change
   */
  const trackTabChange = useCallback(
    (tabId: string, tabLabel?: string) => {
      interactionTracking.trackTabChange(tabId, tabLabel, {
        componentName,
        chapter,
        lessonId
      });
    },
    [componentName, chapter, lessonId]
  );

  /**
   * Track card click (common in dashboards/hubs)
   */
  const trackCardClick = useCallback(
    (cardId: string, cardTitle: string, destination?: string) => {
      interactionTracking.trackCardClick(cardId, cardTitle, destination, {
        componentName,
        chapter,
        lessonId
      });
    },
    [componentName, chapter, lessonId]
  );

  /**
   * Track file download
   */
  const trackDownload = useCallback(
    (fileId: string, fileName: string, fileType?: string) => {
      interactionTracking.trackDownload(fileId, fileName, fileType, {
        componentName,
        chapter,
        lessonId
      });
    },
    [componentName, chapter, lessonId]
  );

  /**
   * Track generic interaction with full control
   * For custom events not covered by specific methods
   */
  const trackInteraction = useCallback(
    (params: Partial<TrackInteractionParams>) => {
      interactionTracking.trackInteraction({
        pagePath: currentPath,
        componentName,
        chapter,
        lessonId,
        ...params
      } as TrackInteractionParams);
    },
    [currentPath, componentName, chapter, lessonId]
  );

  return {
    trackClick,
    trackNavigation,
    trackNavigationOnly,
    trackFormSubmit,
    trackModalOpen,
    trackModalClose,
    trackTabChange,
    trackCardClick,
    trackDownload,
    trackInteraction,
    currentPath,
    sessionId
  };
};

/**
 * Higher-order component wrapper for class components
 * Provides interaction tracking as props
 *
 * @deprecated Use the useInteractionTracking hook in functional components instead
 * NOTE: Commented out due to TSX parsing issues with generics
 * If needed, implement directly in component or use the hook instead
 */
// export function withInteractionTracking(componentName: string, chapter?: string) {
//   return function <P extends object>(Component: React.ComponentType<P & InteractionTrackingHook>) {
//     return function (props: P) {
//       const tracking = useInteractionTracking(componentName, chapter);
//       return <Component {...props} {...tracking} />;
//     };
//   };
// }

/**
 * Hook for tracking page views on mount
 * Useful for analytics without manual tracking
 *
 * @param pageName - Name of the page
 * @param chapter - Chapter context
 *
 * @example
 * ```typescript
 * const MyPage = () => {
 *   usePageViewTracking('Dashboard', 'chapter-0');
 *   return <div>Content</div>;
 * };
 * ```
 */
export const usePageViewTracking = (pageName: string, chapter?: string) => {
  const location = useLocation();

  useMemo(() => {
    interactionTracking.trackInteraction({
      eventType: 'navigation' as InteractionEventType,
      pagePath: location.pathname,
      componentName: pageName,
      chapter,
      metadata: {
        pageView: true,
        timestamp: new Date().toISOString()
      }
    });
  }, [location.pathname, pageName, chapter]);
};

// Export types for external use
export type { InteractionElementType, InteractionEventType, TrackInteractionParams };
